import React, { useMemo, useState } from "react";
import { IconButton, Tooltip, CircularProgress } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/authContext";
import { useSite } from "../../context/siteContext";
import { EXTERNAL_2D_ENABLED, SERVER_BASE_URL } from "../../config";
import { processModelFile, verifyResourceExists } from "../../services/modelService";
import MultipleModelViewer from "../MultipleModelViewer/MultipleModelViewer";
import ModelIcon from "../ModelIcon/ModelIcon";
import ModelPDFIcon from "../ModelPDFIcon/ModelPDFIcon";
import {
	extractFilename,
	selectBestModelFromModelData,
	countAvailableModelsFromModelData,
} from "../../utilities/selectBestModel";


const ModelButton = ({
	machineName,
	machineNumber,
	machineProductName,
	machineid,
	productNumbers,
	modelData = {},
	forcedModel = null
}) => {
	const [loading, setLoading] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);

	const { user } = useAuth();
	const { selectedSite, selectedSiteCurrency } = useSite();
	const navigate = useNavigate();
	const location = useLocation();
	const { t } = useTranslation();
	const returnPath = useMemo(
		() => `${location.pathname}${location.search}${location.hash}`,
		[location.pathname, location.search, location.hash]
	);

	const bestModel = useMemo(() => {
		if (forcedModel) {
			return {
				type: forcedModel.modelType,
				format: forcedModel.format,
				path: forcedModel.filepath.replace(SERVER_BASE_URL, ""),
				filename: forcedModel.filename,
			};
		}
		return selectBestModelFromModelData(modelData);
	}, [forcedModel, modelData]);

	const availableModelCount = useMemo(() => {
		if (forcedModel) return 1;
		return countAvailableModelsFromModelData(modelData);
	}, [forcedModel, modelData]);

	const shouldShowMultipleSelector = !forcedModel && availableModelCount > 1;

	const showError = () =>
		Swal.fire(t('ALERT_MESSAGES.ERROR'), t('GENERAL.FAILED_TO_OPEN_MODEL'), "error");

	const openUrlIfExists = async (url) => {
		if (!url) return false;
		const ok = await verifyResourceExists(url);
		if (!ok) return false;
		window.open(url);
		return true;
	};

	const build2dUrl = (pathValue) => {
		if (!pathValue) return "";
		let normalized = String(pathValue).trim();
		if (!normalized) return "";
		if (/^https?:\/\//i.test(normalized)) return normalized;
		normalized = normalized.replace(/\\/g, "/");
		normalized = normalized.replace(/^https?:\/\/[^/]+/i, "");
		normalized = normalized.replace(/^\/?stock-models\//i, "");
		normalized = normalized.replace(/^\/?models\//i, "");
		normalized = normalized.replace(/^\/?modelfiles\//i, "");
		normalized = normalized.replace(/^\/+/i, "");
		if (!/^2d\//i.test(normalized)) {
			normalized = `2D/${normalized}`;
		}
		if (!/\.pdf$/i.test(normalized)) {
			normalized = `${normalized}.pdf`;
		}
		return `${SERVER_BASE_URL}/stock-models/${normalized}`;
	};

	const handleOpen = async () => {
		if (!bestModel) return showError();

		try {
			if (bestModel.type === "3D" && bestModel.format === "glb") {
				navigate(`/model-viewer/${machineid}/${extractFilename(bestModel.path)}`, {
					state: { fromPath: returnPath },
				});
				return;
			}

			if (bestModel.type === "2D") {
				if (EXTERNAL_2D_ENABLED) {
					const pdfUrl = build2dUrl(bestModel.path);
					if (await openUrlIfExists(pdfUrl)) return;
					showError();
					return;
				}
				navigate(`/2d-model-viewer/${machineid}/${extractFilename(bestModel.path)}`, {
					state: { fromPath: returnPath },
				});
				return;
			}

			if (bestModel.type === "3D" && bestModel.format === "html") {
				setLoading(true);
				const res = await processModelFile({
					filename: extractFilename(bestModel.path),
					machinename: machineName,
					currency: selectedSiteCurrency.toUpperCase(),
					loggedinUser: user.user_id,
					ecommachineid: machineid,
					selectedSite,
					machinenumber: machineNumber,
				});
				if (res?.data?.url) {
					await openUrlIfExists(SERVER_BASE_URL + res.data.url);
				} else showError();
				return;
			}
		} catch {
			showError();
		} finally {
			setLoading(false);
		}
	};

	if (!bestModel) return null;

	return (
		<>
			<Tooltip title={bestModel.type === "3D" ? "View 3D Model" : t('MACHINE_MANAGEMENT.VIEW_2D_DRAWING')}>
				<IconButton
					disabled={loading}
					onClick={(e) => {
						e.stopPropagation();
						shouldShowMultipleSelector ? setModalOpen(true) : handleOpen();
					}}
					sx={{ width: 40, height: 40 }}
				>
					{loading ? (
						<CircularProgress size={20} />
					) : (
						bestModel.type === "3D" ? <ModelIcon /> : <ModelPDFIcon />
					)}
				</IconButton>
			</Tooltip>

			{shouldShowMultipleSelector && (
				<MultipleModelViewer
					show={modalOpen}
					setShow={setModalOpen}
					machine_number={machineNumber}
					machine_name={machineName}
					productNumbers={productNumbers}
					machine_id={machineid}
					machine_product_name={machineProductName}
					modelData={modelData}
				/>
			)}
		</>
	);
};

export default ModelButton;
